import {
  ActivityRowView,
  ActivityTotals,
  CoalitionMerchantActivityRow,
} from '@app/areas/dashboard/models/activity-report.model';

/**
 * Client-side derivation of dashboard metrics. The server returns only raw
 * sums/counts; averages, percentages, and the totals rollup are all computed here.
 */

/** Safe divide: returns 0 when the denominator is 0 (avoids NaN/Infinity in the UI). */
export function div(numerator: number, denominator: number): number {
  return denominator ? numerator / denominator : 0;
}

/** Enrich a raw row with derived metrics. */
export function toRowView(row: CoalitionMerchantActivityRow): ActivityRowView {
  return {
    ...row,
    avgCustomer: div(row.amountTotal, row.customers),
    pctCustMember: div(row.members, row.customers),
    pctTxnsMember: div(row.transactionsMember, row.transactions),
    avgMemberSpend: div(row.amountTotalMember, row.members),
    avgMemberTxn: div(row.amountTotalMember, row.transactionsMember),
    avgNonMemberSpend: div(row.amountTotalNonMember, row.nonMembers),
    avgNonMemberTxn: div(row.amountTotalNonMember, row.transactionsNonMember),
  };
}

export function toRowViews(rows: CoalitionMerchantActivityRow[]): ActivityRowView[] {
  return rows.map(toRowView);
}

/** Roll up the Overall / Members / Non-Members totals over all rows. */
export function computeTotals(rows: ActivityRowView[]): ActivityTotals {
  const sum = (pick: (r: ActivityRowView) => number) => rows.reduce((acc, r) => acc + pick(r), 0);

  const spend = sum((r) => r.amountTotal);
  const customers = sum((r) => r.customers);
  const transactions = sum((r) => r.transactions);

  const memberSpend = sum((r) => r.amountTotalMember);
  const members = sum((r) => r.members);
  const memberTxns = sum((r) => r.transactionsMember);

  const nonMemberSpend = sum((r) => r.amountTotalNonMember);
  const nonMembers = sum((r) => r.nonMembers);
  const nonMemberTxns = sum((r) => r.transactionsNonMember);

  return {
    overall: {
      spend,
      customers,
      avgCustomer: div(spend, customers),
      transactions,
      avgTxn: div(spend, transactions),
    },
    members: {
      spend: memberSpend,
      members,
      avgMember: div(memberSpend, members),
      transactions: memberTxns,
      avgTxn: div(memberSpend, memberTxns),
      pctOfCustomers: div(members, customers),
      pctOfTxns: div(memberTxns, transactions),
    },
    nonMembers: {
      spend: nonMemberSpend,
      nonMembers,
      avgNonMember: div(nonMemberSpend, nonMembers),
      transactions: nonMemberTxns,
      avgTxn: div(nonMemberSpend, nonMemberTxns),
    },
  };
}
